import T from 'prop-types'
/**
PW Tag Input

*/
import React from 'react'
import _ from 'lodash'
import cx from 'classnames'
import Tag from './Tag'
import TypeaheadInput from './TypeaheadInput'


const TAG_STATES = {
  applied: 'applied', // Previously applied tags
  add: 'add', // Existing tag that will be added to the current item on commit
  create: 'create', // New tag that must be created prior to being assigned
  remove: 'remove', // Previously applied tag that will be removed on commit
  preexisting: 'preexisting' // Supplied in the `appliedTags` array, but doesn't have a match in `props.allTags`
}

class TagInput extends React.Component {
  static displayName = 'TagInput'

  static propTypes = {
    allTags: T.array, // List of tags available for fuzzaldrin to search
    allowCreate: T.bool, // Allow tag commits to include new items not found in allTags
    appliedTags: T.array, // List of  applied tags
    className: T.string,
    commitTags: T.func,
    fieldLabel: T.string,
    iconClass: T.string,
    loading: T.bool,
    menuClassName: T.string,
    onClose: T.func,
    onOpen: T.func,
    optionHeight: T.number,
    placeholder: T.string,
    quickFilter: T.func,
    readOnly: T.bool,
    removeTag: T.func,
    renderUnmatchedTags: T.bool, // Render any tags that exist in appliedTags, but _not_ in allTags
    sanitizer: T.func,
    saveTag: T.func,
    selectTag: T.func,
    tagContentComponent: T.any, // Component to use when rendering inner tag content
    tagContentPropTransformer: T.func, // Mapping function that should return props passed to the tagContentComponent. Receives `tag` data as its only argument
    tagSearch: T.func,
    validateOnChange: T.bool,
    validator: T.func,
    valueProp: T.oneOf(['name', 'id']),
    preventAutoSelect: T.bool,
    swallowTab: T.bool,
  }

  static defaultProps = {
    allowCreate: true,
    allTags: [],
    appliedTags: [],
    className: '',
    fieldLabel: 'Add Tag',
    iconClass: 'icon-tag',
    loading: false,
    matchedTags: [],
    menuClassName: '',
    placeholder: '',
    renderUnmatchedTags: false,
    readOnly: false,
    valueProp: 'name',
    preventAutoSelect: true, // Tag input should not auto-select by default.
    swallowTab: false
  }

  constructor(props) {
    super(props)
    this._tagRefs = {}
    this.state = {
      pendingTags: [],
      menuOpen: false
    }
  }

  UNSAFE_componentWillMount() {
    this.tempTagIds = 0 // Incermentable temp tag ID counter
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      pendingTags: _.reject(this.state.pendingTags, pendingTag => {
        const _isApplied = _.some(
          nextProps.appliedTags,
          tagValue => tagValue === pendingTag[this.props.valueProp]
        )
        // If pending removal, remove from pending when _not_ applied
        // If pending add, remove from pending when applied
        return pendingTag._status === TAG_STATES.remove
          ? !_isApplied
          : _isApplied
      })
    })
  }

  getAppliedTags = () => {
    const { props, state } = this
    // if (props.allTags.length === 0) { return [] }
    const _applied = _.reduce(
      props.appliedTags,
      (out, tagValue) => {
        const _pendingTag = _.find(
          state.pendingTags,
          tag => tagValue === tag[this.props.valueProp]
        )
        if (!_pendingTag) {
          const _fullTag = _.find(
            props.allTags,
            tag => tagValue === tag[this.props.valueProp]
          )
          if (_fullTag) {
            // Existing applied tag
            out.push(
              _.assign(_fullTag, {
                _status: TAG_STATES.applied,
                _isPending: false
              })
            )
          } else if (props.renderUnmatchedTags) {
            // Add placeholder for unmatched tag
            const newTagObj = {
              id: 'existing_' + this.tempTagIds++,
              _status: TAG_STATES.preexisting,
              _isPending: false
            }
            newTagObj[this.props.valueProp] = tagValue
            out.push(newTagObj)
          }
          // else {
          //   // Could not match old tag.
          //   // This could be beacuse the tag was deleted from the user-tag autocomplete list (and therefore should not be displayed?)
          //   // Or it could be an API/Machine-added tag that deserves separate treatment/handling.
          //   // console.warn('COULD NOT MATCH OLD TAG')
          // }
        }
        return out
      },
      []
    )
    return _.sortBy(_applied.concat(state.pendingTags), 'name')
  }

  _getAvailableTags = (tags) => {
    const valueProp = this.props.valueProp
    return _.reject(tags, tag => {
      const tagVal = tag[valueProp]
      return (
        _.some(
          this.state.pendingTags,
          pendingTag => pendingTag[valueProp] === tagVal
        ) || _.some(this.props.appliedTags, appliedTag => appliedTag === tagVal)
      )
    })
  }

  _onSave = (newTag) => {
    if (this.props.allowCreate) {
      // Add a temporary ID to the new tag
      this._applyTagPending(
        _.assign(newTag, {
          id: 'new_' + this.tempTagIds++
        }),
        true
      )
    }
  }

  _onTypeaheadChange = (tag) => {
    if (tag.isNew) {
      this._onSave(tag)
    } else {
      this._applyTagPending(tag)
    }
  }

  _applyTagPending = (tag, isNew = false) => {
    // Double check that this tag is not already applied
    const _existing = _.find(this.state.pendingTags, { name: tag.name })

    if (_existing) {
      console.warn('Tried to add an already applied tag', tag)
    } else {
      const _tags = this.state.pendingTags || []

      const _newTag = _.assign(tag, {
        _status: isNew === true ? TAG_STATES.create : TAG_STATES.add,
        _isPending: true
      })
      _tags.push(_newTag)
      this.setState({
        pendingTags: _tags
      })
      const _commit = {}
      _commit[isNew === true ? TAG_STATES.create : TAG_STATES.add] = [_newTag]
      this._commitTags(_commit)
    }
  }

  _removeTag = (tag) => {
    const _prevPending = this.state.pendingTags
    _prevPending.push(
      _.assign(tag, {
        _status: TAG_STATES.remove,
        _isPending: true
      })
    )

    this.setState({
      pendingTags: _prevPending
    })

    const _commit = {}
    _commit[TAG_STATES.remove] = [tag]
    this._commitTags(_commit)
  }

  // tagsGroupedByUpdateAction shoudd have this shape:
  // { create: [], add: [], remove: [] }
  // with each array containing either tag strings or tag IDs
  _commitTags = (tagCommit) => {
    const _commitFn = this.props.commitTags
    if (_commitFn && tagCommit) {
      _commitFn(_.omit(tagCommit, TAG_STATES.applied)) // Ensure applied group does not get passed
    }
  }

  _onMenuClose = () => {
    if (this.props.onClose) {
      this.props.onClose()
    }
    this.setState({ menuOpen: false })
  }

  _onMenuOpen = () => {
    if (this.props.onOpen) {
      this.props.onOpen()
    }
    this.setState({ menuOpen: true })
  }

  focus = () => {
    if (this._inputRef && !this.props.readOnly) {
      this._inputRef.focus()
    }
  }

  _bindTypeaheadRef = (input) => {
    this._inputRef = input
  }

  _bindTagRef = (tagId, el) => {
    this._tagRefs[tagId] = el
  }

  render() {
    const { state, props } = this

    const _isDirty = state.pendingTags.length > 0

    const classes = cx('tag_input', {
      loading: props.loading,
      readonly: props.readOnly,
      'menu_open has_focus': state.menuOpen || _isDirty
    })

    return (
      <div className={classes + ' ' + props.className}>
        <div
          className={cx('main_tag_icon icon', props.iconClass)}
          onClick={this.focus}
        />
        {_.map(this.getAppliedTags(), tag => {
          return (
            <Tag
              key={'ap_' + tag.id}
              onRemove={props.readOnly ? null : this._removeTag}
              ref={_.partial(this._bindTagRef, tag.id)}
              removeTooltip="Remove"
              tag={tag}
              tagContentComponent={props.tagContentComponent}
              tagContentPropTransformer={props.tagContentPropTransformer}
            />
          )
        })}

        {!props.elementMode
          ? null
          : React.createElement(
              props.elementMode.element,
              props.elementMode.props
            )}

        {props.readOnly || props.elementMode
          ? null
          : <div className="tag_input_wrap">
              <TypeaheadInput
                {..._.pick(
                  props,
                  'menuClassName',
                  'optionHeight',
                  'placeholder',
                  'readOnly',
                  'sanitizer',
                  'valueProp',
                  'preventAutoSelect',
                  'swallowTab'
                )}
                className="grid_input tag_input_el"
                omitCaret
                onChange={this._onTypeaheadChange}
                onMenuClose={this._onMenuClose}
                onMenuOpen={this._onMenuOpen}
                options={this._getAvailableTags(props.allTags)}
                ref={this._bindTypeaheadRef}
                saveOnBlur
                validateOnChange={props.validateOnChange}
                validator={props.validator}
              />
              {state.menuOpen
                ? null
                : <div className="add_btn btn btn-mini" onClick={this.focus}>
                    <span className="add_text">
                      {props.fieldLabel}
                    </span>
                  </div>}
            </div>}
      </div>
    )
  }
}


export default TagInput
